import { Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { ColorsConstants } from "../../../../cssUtils/constants";
import { formatTitle } from "../utils";

export const ArrayObjectField = ({ label, itemLabel = "Item", value = [], schema, onChange }) => {
  const properties = schema?.items?.properties;
  const minItems = schema?.minItems ?? 0;
  const maxItems = schema?.maxItems ?? Infinity;

  const handleItemChange = (index, key, newValue) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [key]: newValue };
    onChange(updated);
  };

  const addItem = () => {
    const emptyItem = Object.keys(properties).reduce((acc, key) => {
      acc[key] = properties[key].default ?? "";
      return acc;
    }, {});
    onChange([...(value || []), emptyItem]);
  };

  const removeItem = (index) => {
    const updated = value.filter((_, i) => i !== index);
    onChange(updated);
  };

  const canAdd = value.length < maxItems;
  const canRemove = value.length > minItems;

  return (
    <Box>
      {label && <Typography sx={{ fontWeight: "bold", mb: 2 }}>{label}</Typography>}

      <Box sx={{px:1}}>
        {value.map((item, index) => (
          <Box
            key={index}
            sx={{
              borderBottom: "1px solid #ddd",
              mb: 1.5,
              pb: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: ColorsConstants.primaryBlue100 }}
              >
                {itemLabel} {index + 1}
              </Typography>

              <Box
                sx={{
                  cursor: canRemove ? "pointer" : "not-allowed",
                  opacity: canRemove ? 1 : 0.3,
                }}
                onClick={() => canRemove && removeItem(index)}
              >
                <Close fontSize="small" />
              </Box>
            </Box>

            <Stack spacing={2}>
              {Object.entries(properties).map(([key, propSchema]) =>
                propSchema.type === "boolean" ? (
                  <TextField
                    key={key}
                    select
                    label={formatTitle(key)}
                    value={item[key] ?? propSchema.default ?? false}
                    onChange={(e) => handleItemChange(index, key, e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </TextField>
                ) : propSchema.enum ? (
                  <TextField
                    key={key}
                    select
                    label={formatTitle(key)}
                    value={item[key] ?? propSchema.default ?? ""}
                    onChange={(e) => handleItemChange(index, key, e.target.value)}
                    size="small"
                    fullWidth
                  >
                    {propSchema.enum.map((option) => (
                      <MenuItem key={option} value={option}>
                        {formatTitle(option)}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    key={key}
                    label={formatTitle(key)}
                    type={propSchema.type === "number" ? "number" : "text"}
                    value={item[key] ?? ""}
                    onChange={(e) =>
                      handleItemChange(
                        index,
                        key,
                        propSchema.type === "number"
                          ? Number(e.target.value)
                          : e.target.value,
                      )
                    }
                    size="small"
                    fullWidth
                  />
                ),
              )}
            </Stack>
          </Box>
        ))}

        {canAdd && (
          <Box
            onClick={addItem}
            sx={{
              cursor: "pointer",
              color: ColorsConstants.primaryBlue100,
              fontWeight: "bold",
            }}
          >
            + Add
          </Box>
        )}
        {!canAdd && (
          <Typography variant="caption" sx={{ color: ColorsConstants.grayFontColor }}>
            Maximum {maxItems} items reached
          </Typography>
        )}
      </Box>
    </Box>
  );
};
